import { useState, useEffect, useRef } from "react";
import { PricingModal } from "@/ui-components/PricingModal";
import { getTrialCtaLabel } from "@/utils/currency";
import {
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  LockClosedIcon,
} from "@heroicons/react/20/solid";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import ReactDOM from "react-dom";
import { useStore } from "../../stores/store";
import { useSquawkPlayer } from "./SquawkPlayerContext";
import { channels } from "./squawkChannels";
import { useAccessControl } from "../../hooks/useAccessControl";

const SquawkPlayerButton = () => {
  const desktopSidebarOpen = useStore((s) => s.desktopSidebarOpen);
  const { authenticated, keycloakInstance, isInDemoMode, isChannelRoleLocked } = useAccessControl();
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  // Get player state and methods from context
  const {
    selectedChannel,
    status,
    volume,
    handleChannelSelect,
    handleVolumeChange,
    preferredPlayer,
  } = useSquawkPlayer();

  // Consolidated visual state management
  // Prevents flashing UI during quick connections by delaying visual updates
  const [visualState, setVisualState] = useState({
    status: status,
    channel: selectedChannel,
  });

  const visualTimerRef = useRef(null);
  const prevStatusRef = useRef(status);

  useEffect(() => {
    // Clear any pending visual updates
    if (visualTimerRef.current) {
      clearTimeout(visualTimerRef.current);
      visualTimerRef.current = null;
    }

    const becameConnected = prevStatusRef.current !== "connected" && status === "connected";
    prevStatusRef.current = status;

    // Immediate updates (no delay):
    // 1. When connection completes successfully
    // 2. When channel is deselected
    // 3. When status changes to error or disconnected
    if (becameConnected || !selectedChannel || status === "error" || status === "disconnected") {
      setVisualState({
        status: status,
        channel: selectedChannel,
      });
      return;
    }

    // Delayed updates (500ms):
    // 1. When transitioning to "connecting"
    // 2. When channel changes while already connecting
    // This prevents flashing spinner on fast connections
    if (status === "connecting" || selectedChannel) {
      visualTimerRef.current = setTimeout(() => {
        setVisualState({
          status: status,
          channel: selectedChannel,
        });
      }, 500);
    } else {
      // For idle state, update immediately
      setVisualState({
        status: status,
        channel: selectedChannel,
      });
    }

    return () => {
      if (visualTimerRef.current) {
        clearTimeout(visualTimerRef.current);
        visualTimerRef.current = null;
      }
    };
  }, [status, selectedChannel]);

  const visualStatus = visualState.status;
  const displayedChannel = visualState.channel;

  const buttonClasses =
    visualStatus === "error"
      ? "text-white font-bold bg-red-600 shadow-md shadow-red-950/40 animate-bounce"
      : visualStatus === "connected"
        ? "text-white font-semibold bg-green-500 shadow-md shadow-green-900/30"
        : visualStatus === "connecting"
          ? "text-gray-900 font-semibold bg-yellow-400 shadow-md shadow-yellow-900/20 animate-pulse"
          : "text-white font-semibold bg-red-800 shadow-md shadow-red-950/50 hover:bg-red-700 transition-all duration-200";

  return (
    <>
      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} />
      <Popover className="">
      <PopoverButton
        className={`${
          desktopSidebarOpen
            ? "flex flex-row rounded-sm text-1.5xs lg:flex-col items-center justify-start lg:justify-center text-center w-full px-8 lg:px-0 overflow-hidden"
            : "flex flex-row rounded-md sm:rounded font-semibold items-center justify-start w-auto mr-2 px-3 sm:px-4 py-1.5 overflow-hidden whitespace-nowrap"
        } focus:outline-none ${buttonClasses}`}
      >
        <div
          className={`${
            desktopSidebarOpen ? "-m-2 mr-2 lg:m-0 lg:mr-0" : "-m-2 mr-2"
          }`}
        >
          {status === "connected" ? (
            <>
              {desktopSidebarOpen && <span className="hidden lg:block">Playing</span>}
              <SpeakerWaveIcon
                className="h-6 w-6 shrink-0 mx-auto"
                aria-hidden="true"
              />
            </>
          ) : (
            <>
              {desktopSidebarOpen && <span className="hidden lg:block">Start</span>}
              <SpeakerXMarkIcon
                className="h-6 w-6 shrink-0 mx-auto"
                aria-hidden="true"
              />
            </>
          )}
        </div>
        <span>Squawk</span>
      </PopoverButton>

      <Transition
        as="div"
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        {ReactDOM.createPortal(
          <PopoverPanel
            as="div"
            className={`absolute z-50 w-72 max-w-xs ${
              desktopSidebarOpen ? "lg:left-6 lg:top-24 lg:ml-4" : ""
            } right-16 mt-2 top-5`}
          >
            <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="relative grid gap-6 bg-gray-800 p-5">
                {/* Volume slider */}
                <div className="flex items-center space-x-2 pb-2 border-b border-gray-600">
                  <SpeakerWaveIcon className="h-5 w-5 text-white" />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-300">
                    {Math.round(volume * 100)}%
                  </span>
                </div>

                {isInDemoMode && (
                  <div className="border-b border-gray-600 pb-4 mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terminal-orange opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-terminal-orange" />
                      </span>
                      <span className="text-xs font-semibold text-terminal-orange uppercase">Demo</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">
                      Listening to <span className="text-terminal-orange">24-hour delayed</span> recordings
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setIsPricingOpen(true)}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-squawk-blue hover:bg-squawk-blue/80 rounded-md transition-colors cursor-pointer"
                      >
                        {getTrialCtaLabel()}
                      </button>
                      {!authenticated && (
                        <button
                          onClick={() => keycloakInstance?.login()}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700/40 hover:bg-gray-600 rounded-md transition-colors cursor-pointer"
                        >
                          Login
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* TODO(demo-playback): Remove this notice when demo playback is restored. */}
                {isInDemoMode && (
                  <div className="mb-2 p-2 text-xs text-gray-400 bg-gray-800/60 rounded border border-gray-700">
                    Demo playback temporarily unavailable.
                  </div>
                )}

                {/* Channel selection */}
                {channels
                  .filter((channel) => !channel.v2Only || preferredPlayer === "v2")
                  // Audio Test has no v2 publisher (asset:audio_test isn't in the v2
                  // listener token), so on v2 it connects but stays silent. Hide it there.
                  .filter((channel) => !(channel.v2Excluded && preferredPlayer === "v2"))
                  .map((channel) => {
                  const isDisplayed = displayedChannel?.name === channel.name;
                  // Are we in the lag period (displayed channel hasn't caught up to selected)?
                  const isInLagPeriod = displayedChannel !== null && displayedChannel?.name !== selectedChannel?.name;
                  // Show stop icon if: truly connected, OR in lag period (keeping old channel visible)
                  const shouldShowStop = isDisplayed && (status === "connected" || isInLagPeriod);
                  // Show spinner only if displayed and actively connecting (after lag period)
                  const shouldShowSpinner = isDisplayed && status === "connecting" && !isInLagPeriod;

                  // TODO(demo-playback): Restore channel-level demo access when the backend audio
                  // file serving is restored. The /api/squawk_history JSON returns audio paths
                  // that the upstream does not currently serve (all /squawks/* paths 404). Until
                  // then, every channel is greyed out in demo mode.
                  // Previously: isInDemoMode && (!channel.demoChannel || channel.name === "Audio Test")
                  const isDemoDisabled = isInDemoMode;

                  // Role-based access: only applies to subscribers, internal users bypass
                  const isRoleLocked = isChannelRoleLocked(channel.requiredRole);

                  const isDisabled = isDemoDisabled || isRoleLocked;

                  return (
                    <div
                      key={channel.name}
                      className={`flex items-start p-3 bg-gray-800 rounded-lg transition-all duration-150 ${
                        isDisabled
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-gray-600 cursor-pointer active:scale-95 active:bg-gray-700"
                      }`}
                      onClick={() => !isDisabled && handleChannelSelect(channel)}
                    >
                      {isRoleLocked ? (
                        <LockClosedIcon
                          className="h-5 w-5 text-terminal-orange"
                          aria-hidden="true"
                        />
                      ) : shouldShowStop ? (
                        <StopIcon
                          className="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                      ) : shouldShowSpinner ? (
                        <ArrowPathIcon
                          className="h-5 w-5 text-yellow-400 animate-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <PlayIcon
                          className={`h-5 w-5 ${isDisabled ? "text-gray-500" : "text-green-500"}`}
                          aria-hidden="true"
                        />
                      )}
                      <div className="ml-4">
                        <p className={`text-xs font-medium ${isDisabled ? "text-gray-500" : "text-white"}`}>
                          {channel.name}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          {channel.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </PopoverPanel>,
          document.body
        )}
      </Transition>
    </Popover>
    </>
  );
};

export default SquawkPlayerButton;
